import { QrCode, ArrowRight, BookOpen, Brain, Calendar, Clock, Compass, Heart, LucideIcon, Users } from "lucide-react";

export default function ProgramPDFTemplate() {
  return (
    <div className="bg-white text-charcoal font-serif">
      {/* Header Section */}
      <header className="text-center mb-12">
        <img 
          src="/lovable-uploads/0788554f-dbfe-44f8-bd67-7e7e91abbb3c.png" 
          alt="The Monastery Method™" 
          className="w-24 h-24 object-contain mx-auto mb-6"
        />
        <h1 className="text-3xl mb-3 tracking-wide">THE MONASTERY METHOD™</h1>
        <h2 className="text-xl text-charcoal/80 font-light">Ancient Wisdom for Modern Leadership</h2>
      </header>

      {/* Journey Section - UPDATED */}
      <SectionWithIcon title="THE JOURNEY" icon={Compass}>
        <p className="text-base text-center max-w-2xl mx-auto leading-relaxed mb-8">
          The Monastery Method™ guides participants through a transformative 7-week journey from the chaos of modern life to the freedom of authentic living. This daily, hands-on experience moves through three sacred stages: departing from noise, encountering wisdom in the desert, and returning with renewed clarity, purpose, and sustainable peace—without leaving your place in the world. Like the technological innovations that transformed how we live, this method reimagines ancient wisdom for those who seek meaning beyond mere success.
        </p>
      </SectionWithIcon>

      {/* Path Section */}
      <SectionWithIcon title="YOUR PATH" icon={BookOpen}>
        <div className="space-y-8">
          <PathStage 
            week="WEEK 1" 
            title="LEAVING THE METROPOLIS"
            quote="The most dangerous form of success is the kind that fills your bank account while emptying your soul."
            items={[
              "Examine the true nature of freedom",
              "Break free from mimetic desire",
              "Begin the simple practice of morning silence"
            ]}
          />
          
          <PathStage 
            week="WEEKS 2-4" 
            title="THE DESERT EXPERIENCE"
            quote="What got you here won't get you there."
            items={[
              "Discover freedom from compulsive desires",
              "Release the addiction to recognition",
              "Let go of the illusion of control",
              "Establish transformative daily practices"
            ]}
          />
          
          <PathStage 
            week="WEEKS 5-6" 
            title="THE MONASTERY"
            quote="Peace is not something to pursue directly, but an outcome of living differently."
            items={[
              "Transform your closest relationships",
              "Bring renewed presence to your work",
              "Develop the art of sacred conversation",
              "Experience the joy of authentic connection"
            ]}
          />
          
          <PathStage 
            week="WEEK 7" 
            title="RETURN TO THE WORLD"
            quote="True success isn't about what you achieve. It's about who you become."
            items={[
              "Integrate all practices into daily life",
              "Establish your personalized rule of life",
              "Live from a place of authentic freedom",
              "Experience sustainable peace amid achievement"
            ]}
          />
        </div>
      </SectionWithIcon>

      {/* Program Elements */}
      <SectionWithIcon title="PROGRAM ELEMENTS" icon={Clock}>
        <div className="grid grid-cols-3 gap-6">
          <ProgramElement 
            title="DAILY RHYTHM" 
            icon={Calendar}
            items={[
              "Morning practice (10 minutes)",
              "Midday renewal (10 minutes)",
              "Evening integration (10 minutes)",
              "Simple, sustainable practices"
            ]}
          />
          
          <ProgramElement 
            title="WEEKLY GATHERING" 
            icon={Users}
            items={[
              "Small group connection",
              "Shared experience",
              "Personal guidance",
              "Community support"
            ]}
          />
          
          <ProgramElement 
            title="TRANSFORMATIVE PRACTICES" 
            icon={Brain}
            items={[
              "Sacred pause",
              "Presence cultivation",
              "Pattern recognition",
              "Paradox integration",
              "Purpose alignment"
            ]}
          />
        </div>
      </SectionWithIcon>

      {/* Outcomes Section */}
      <SectionWithIcon title="THE OUTCOMES" icon={Heart}>
        <p className="text-base mb-6 text-center">Participants emerge from The Monastery Method™ with:</p>
        
        <div className="space-y-4 max-w-xl mx-auto">
          <OutcomeItem label="Mental clarity" description="that cuts through noise and distraction" />
          <OutcomeItem label="Emotional freedom" description="from the tyranny of comparison" />
          <OutcomeItem label="Spiritual depth" description="that transcends circumstance" />
          <OutcomeItem label="Relational authenticity" description="in family and professional life" />
          <OutcomeItem label="Purposeful direction" description="aligned with deepest values" />
        </div>
      </SectionWithIcon>

      {/* For Whom Section - UPDATED */}
      <SectionWithIcon title="ELIGIBILITY REQUIREMENTS" icon={Users}>
        <p className="text-base mb-4 text-center">The Monastery Method isn't for everyone—it's for accomplished leaders seeking deeper integration of success and meaning. This program is for you if:</p>
        <ul className="list-disc pl-6 space-y-2 max-w-xl mx-auto">
          <li>You're a leader with significant responsibility (executive, business owner, or high-impact professional).</li>
          <li>You've achieved recognized success in your field for at least five years.</li>
          <li>You sense that greater achievement alone won't bring the fulfillment you seek.</li>
          <li>You're ready to invest in transforming your relationship with success, meaning, and purpose.</li>
        </ul>
      </SectionWithIcon>

      {/* Guide Section */}
      <SectionWithIcon title="YOUR GUIDE" icon={Compass}>
        <div className="flex items-center gap-8 max-w-xl mx-auto">
          <img 
            src="/lovable-uploads/20b29789-0c27-4ca5-bc2c-8738168184c4.png" 
            alt="Tommy Zee" 
            className="w-24 h-24 object-cover rounded-full"
          />
          <p className="text-charcoal/90 leading-relaxed">
            Drawing from 20+ years working with global brands across three continents while studying timeless wisdom traditions, your guide has integrated ancient monastic principles with modern leadership for a unique approach to sustainable success with meaning.
          </p>
        </div>
      </SectionWithIcon>

      {/* Application Section */}
      <section className="mb-10">
        <div className="text-center">
          <h2 className="text-xl font-serif mb-6 tracking-wide">Limited to 12 Leaders | Apply Now</h2>
          
          <div className="mb-8">
            <p className="text-lg italic text-charcoal/90">
              "For what will it profit a man if he gains the whole world, but loses his soul?"
            </p>
          </div>
          
          <div className="border-2 border-charcoal/10 p-6 mx-auto max-w-md rounded-sm">
            <h3 className="text-lg font-serif mb-4 flex items-center justify-center">
              <QrCode className="h-5 w-5 mr-2 text-charcoal/70" />
              HOW TO APPLY
            </h3>
            
            <p className="mb-4 text-charcoal/90">
              To submit your application for The Monastery Method™, visit:
            </p>
            
            <p className="text-xl font-medium mb-4 border-b border-t py-3 border-charcoal/10">
              themonasterymethod.com/apply
            </p>
            
            <p className="text-sm text-charcoal/70">
              Applications are reviewed within 48 hours. Questions can be directed to knock@themonasterymethod.com
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center mt-12 pt-6 border-t border-charcoal/10">
        <p className="text-charcoal/60 text-sm">
          © The Monastery Method™ | themonasterymethod.com
        </p>
      </footer>
    </div>
  );
}

// Helper Components
const SectionWithIcon = ({ title, icon: Icon, children }: { title: string; icon: LucideIcon; children: React.ReactNode }) => {
  return (
    <section className="mb-12">
      <h3 className="text-xl font-serif mb-6 text-center tracking-wide flex items-center justify-center">
        <Icon className="h-5 w-5 mr-2 text-charcoal/80" />
        {title}
      </h3>
      {children}
    </section>
  );
};

const PathStage = ({ week, title, quote, items }: { week: string; title: string; quote: string; items: string[] }) => {
  return (
    <div>
      <h4 className="text-lg font-serif mb-2">{week}: {title}</h4>
      <p className="text-base italic text-charcoal/90 mb-3">
        "{quote}"
      </p>
      <ul className="list-disc pl-6 text-charcoal/90 space-y-1">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const ProgramElement = ({ title, icon: Icon, items }: { title: string; icon: LucideIcon; items: string[] }) => {
  return (
    <div className="bg-gray-50 p-5 shadow-sm">
      <h4 className="text-lg font-serif mb-3 flex items-center">
        <Icon className="h-4 w-4 mr-2 text-charcoal/70" />
        {title}
      </h4>
      <ul className="text-charcoal/90 space-y-2 text-sm">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const OutcomeItem = ({ label, description }: { label: string; description: string }) => {
  return (
    <div className="flex items-start">
      <div className="mr-3 pt-1">
        <div className="w-1 h-1 rounded-full bg-charcoal"></div>
      </div>
      <p className="text-charcoal/90">
        <span className="font-medium">{label}</span> {description}
      </p>
    </div>
  );
};
